import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HelpCircle, Compass, Lightbulb, Sparkles, Rocket } from "lucide-react";

const T = {
  fr: {
    eyebrow: "la boucle de la curiosité",
    title: "Une question en appelle une autre",
    intro: "Cocoon Mind ne referme jamais le savoir : il l'ouvre. Chaque réponse rallume une question, et le cercle tourne, sans fin — comme un nœud sans début ni bout.",
    slogan: "La curiosité est l'engrais du savoir, et le savoir est le carburant de la curiosité.",
    center: "Sans fin",
    steps: [
      { t: "Curiosité", d: "Quelque chose t'intrigue." },
      { t: "Question", d: "Tu oses la formuler." },
      { t: "Exploration", d: "Tu creuses avec L'Élan." },
      { t: "Compréhension", d: "Le savoir s'ancre en toi." },
      { t: "Nouvelles idées", d: "Qui rouvrent la curiosité." },
    ],
  },
  en: {
    eyebrow: "the curiosity loop",
    title: "One question opens the next",
    intro: "Cocoon Mind never closes knowledge — it opens it. Every answer reignites a question, and the circle keeps turning, endlessly — like a knot with no beginning or end.",
    slogan: "Curiosity is the fertiliser of knowledge, and knowledge is the fuel of curiosity.",
    center: "Endless",
    steps: [
      { t: "Curiosity", d: "Something intrigues you." },
      { t: "Question", d: "You dare to ask it." },
      { t: "Exploration", d: "You dig in with L'Élan." },
      { t: "Understanding", d: "Knowledge takes root." },
      { t: "New ideas", d: "That reopen curiosity." },
    ],
  },
  es: {
    eyebrow: "el bucle de la curiosidad",
    title: "Una pregunta lleva a otra",
    intro: "Cocoon Mind nunca cierra el saber: lo abre. Cada respuesta reaviva una pregunta, y el círculo sigue girando, sin fin — como un nudo sin principio ni final.",
    slogan: "La curiosidad es el abono del saber, y el saber es el combustible de la curiosidad.",
    center: "Sin fin",
    steps: [
      { t: "Curiosidad", d: "Algo te intriga." },
      { t: "Pregunta", d: "Te atreves a formularla." },
      { t: "Exploración", d: "Indagas con L'Élan." },
      { t: "Comprensión", d: "El saber se arraiga." },
      { t: "Nuevas ideas", d: "Que reabren la curiosidad." },
    ],
  },
  nl: {
    eyebrow: "de nieuwsgierigheidslus",
    title: "De ene vraag opent de volgende",
    intro: "Cocoon Mind sluit kennis nooit af — het opent haar. Elk antwoord wekt een nieuwe vraag, en de cirkel blijft draaien, eindeloos — als een knoop zonder begin of eind.",
    slogan: "Nieuwsgierigheid is de mest van kennis, en kennis is de brandstof van nieuwsgierigheid.",
    center: "Eindeloos",
    steps: [
      { t: "Nieuwsgierigheid", d: "Iets prikkelt je." },
      { t: "Vraag", d: "Je durft ze te stellen." },
      { t: "Verkenning", d: "Je graaft met L'Élan." },
      { t: "Begrip", d: "Kennis wortelt zich." },
      { t: "Nieuwe ideeën", d: "Die nieuwsgierigheid heropenen." },
    ],
  },
};

const ICONS = [Sparkles, HelpCircle, Compass, Lightbulb, Rocket];

// 5 nodes evenly placed around the circle, starting at the top (inside the orbit ring).
const R = 37; // % radius for the orbiting discs
const RING = 45; // % radius for the orbit ring (nodes sit just inside)
const NODES = [0, 1, 2, 3, 4].map((i) => {
  const a = ((-90 + i * 72) * Math.PI) / 180;
  return { x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a) };
});

// Lemniscate (∞) — the "endless knot" at the core.
const KNOT = "M50,50 C50,39 30,39 30,50 C30,61 50,61 50,50 C50,39 70,39 70,50 C70,61 50,61 50,50 Z";

function Wheel({ steps }) {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[420px] my-4" data-testid="boucle-wheel">
      {/* soft magnetic glow */}
      <div aria-hidden className="absolute inset-[14%] rounded-full blur-[60px] pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(32 96% 56% / 0.22), transparent 70%)" }} />

      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          <linearGradient id="bc-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="hsl(42 96% 66%)" />
            <stop offset="55%" stopColor="hsl(32 96% 56%)" />
            <stop offset="100%" stopColor="hsl(24 92% 46%)" />
          </linearGradient>
          <radialGradient id="bc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(38 96% 60% / 0.55)" />
            <stop offset="100%" stopColor="hsl(38 96% 60% / 0)" />
          </radialGradient>
          <path id="bc-orbit" d="M50,5 a45,45 0 1,1 -0.01,0" fill="none" />
          <filter id="bc-blur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.1" />
          </filter>
        </defs>

        {/* faint base ring */}
        <circle cx="50" cy="50" r="45" fill="none" stroke="hsl(38 40% 70% / 0.14)" strokeWidth="0.6" />

        {/* slow rotating dashed ring */}
        <g style={{ transformOrigin: "50px 50px" }}>
          <circle cx="50" cy="50" r="45" fill="none" stroke="hsl(38 90% 60% / 0.30)" strokeWidth="0.7"
            strokeDasharray="1.4 5" strokeLinecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="60s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* bright sweeping arc — the magnetic pull */}
        <circle cx="50" cy="50" r="45" fill="none" stroke="url(#bc-gold)" strokeWidth="1.1"
          strokeLinecap="round" strokeDasharray="29 254">
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="9s" repeatCount="indefinite" />
        </circle>

        {/* comet travelling the loop */}
        <circle r="1.7" fill="url(#bc-gold)" filter="url(#bc-blur)">
          <animateMotion dur="9s" repeatCount="indefinite" rotate="auto">
            <mpath href="#bc-orbit" />
          </animateMotion>
        </circle>

        {/* endless knot core */}
        <circle cx="50" cy="50" r="18" fill="url(#bc-core)" />
        <path d={KNOT} fill="none" stroke="hsl(38 30% 80% / 0.18)" strokeWidth="2.4" />
        <path d={KNOT} fill="none" stroke="url(#bc-gold)" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="7 5">
          <animate attributeName="stroke-dashoffset" from="0" to="-48" dur="3.2s" repeatCount="indefinite" />
        </path>
        {/* light bead flowing along the knot */}
        <circle r="1.4" fill="hsl(46 100% 80%)" filter="url(#bc-blur)">
          <animateMotion dur="4.6s" repeatCount="indefinite">
            <mpath href="#bc-knot-path" />
          </animateMotion>
        </circle>
        <path id="bc-knot-path" d={KNOT} fill="none" stroke="none" />
      </svg>

      {/* orbiting nodes */}
      {steps.map((s, i) => {
        const Icon = ICONS[i] || Sparkles;
        const p = NODES[i];
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            data-testid={`boucle-node-${i}`}
          >
            <motion.div
              className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center border border-primary/40 bg-background/80 backdrop-blur-md"
              animate={{ boxShadow: ["0 0 0px hsl(32 96% 56% / 0)", "0 0 22px hsl(32 96% 56% / 0.5)", "0 0 0px hsl(32 96% 56% / 0)"] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: i * 0.45 }}
            >
              <Icon size={22} strokeWidth={1.7} className="text-primary" />
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-background text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_hsl(32_96%_56%/0.6)]">
                {i + 1}
              </span>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
}

export function BoucleCuriosite() {
  const { i18n } = useTranslation();
  const c = T[(i18n.language || "fr").slice(0, 2)] || T.fr;

  return (
    <section className="relative px-5 sm:px-6 py-20" data-testid="boucle-curiosite">
      <div className="relative max-w-4xl mx-auto">
        <div className="relative overflow-hidden rounded-[2rem] border border-primary/20 px-5 sm:px-12 py-14 sm:py-16"
          style={{ background: "linear-gradient(180deg, hsl(32 96% 56% / 0.06), transparent 55%)" }}>
          <div className="grain-overlay" aria-hidden />
          <div aria-hidden className="absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[420px] rounded-full blur-[110px] pointer-events-none"
            style={{ background: "radial-gradient(circle, hsl(32 96% 56% / 0.12), transparent 70%)" }} />

          <div className="relative text-center">
            <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2 text-primary/80 tracking-[0.28em] uppercase text-xs mb-5">
              <Sparkles size={14} /> {c.eyebrow}
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight max-w-3xl mx-auto" data-testid="boucle-title">
              {c.title}
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-5 text-foreground/60 leading-[1.8] max-w-2xl mx-auto">
              {c.intro}
            </motion.p>

            {/* La roue — nœud sans fin */}
            <div className="relative">
              <Wheel steps={c.steps} />
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden sm:block">
                <span className="font-display text-gradient-orange text-xs tracking-[0.34em] uppercase" data-testid="boucle-center">
                  {c.center}
                </span>
              </div>
            </div>

            {/* Légende des étapes */}
            <div className="mt-10 grid gap-3 sm:grid-cols-2 max-w-2xl mx-auto text-left" data-testid="boucle-legend">
              {c.steps.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.02] px-4 py-3.5 ${i === 4 ? "sm:col-span-2 sm:max-w-[calc(50%-0.375rem)] sm:mx-auto" : ""}`}
                  data-testid={`boucle-legend-${i}`}
                >
                  <span className="mt-0.5 w-7 h-7 flex-shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-display text-foreground/90 leading-tight">{s.t}</p>
                    <p className="mt-0.5 text-foreground/50 text-sm leading-snug">{s.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.blockquote initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-12 max-w-2xl mx-auto font-display italic text-gradient-orange text-2xl sm:text-3xl leading-snug" data-testid="boucle-slogan">
              « {c.slogan} »
            </motion.blockquote>
          </div>
        </div>
      </div>
    </section>
  );
}
